import { ApiClientError, type FreeAllowanceRefusalResponse } from '@contract-analyzer/api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { QuotaBlockDialog } from '@/components/quota-block-dialog';
import {
  QuotaBlockCtx,
  type QuotaBlockContextValue,
  type QuotaResource,
} from '@/hooks/quota-block-context';
import { useUploadBatchStore } from '@/hooks/upload-batch-context';

const QUOTA_CODE_TO_RESOURCE: Readonly<Record<string, QuotaResource>> = {
  'tenant-document-quota-exceeded': 'documents',
  'tenant-analysis-run-quota-exceeded': 'analysis runs',
  'tenant-extraction-quota-exceeded': 'extractions',
};

const AllowanceCount = z.number().int().nonnegative();
/** The 402 body's account of where the free allowance stood when it refused. */
const AllowanceRefusal = z.object({
  allowance: z.object({ limit: AllowanceCount, used: AllowanceCount, requested: AllowanceCount }),
}) satisfies z.ZodType<{ readonly allowance: FreeAllowanceRefusalResponse }>;

interface QuotaRefusal {
  readonly resource: QuotaResource;
  readonly allowance: FreeAllowanceRefusalResponse | null;
}

export function QuotaBlockProvider({ children }: { readonly children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [refusal, setRefusal] = useState<QuotaRefusal>({ resource: 'documents', allowance: null });

  const handleQuotaError = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof ApiClientError) || error.status !== 402) {
        return false;
      }
      if (error.code === null) return false;
      const mapped = QUOTA_CODE_TO_RESOURCE[error.code];
      if (mapped === undefined) return false;
      const stated = AllowanceRefusal.safeParse(error.details);
      setRefusal({ resource: mapped, allowance: stated.success ? stated.data.allowance : null });
      setOpen(true);
      return true;
    },
    [],
  );
  const openExtractionAllowanceBlock = useCallback(
    (allowance: FreeAllowanceRefusalResponse) => {
      setRefusal({ resource: 'extractions', allowance });
      setOpen(true);
    },
    [],
  );

  // Transfers outlive the page or sheet that started them, so a refused upload
  // session is caught here, where the workspace's dialog lives. An upload's
  // other files are refused for the same reason, so each upload opens it once.
  const uploads = useUploadBatchStore();
  useEffect(() => {
    const refused = new Set<string>();
    return uploads.onTransferFailed(({ batchId, failure }) => {
      if (!refused.has(batchId) && handleQuotaError(failure)) refused.add(batchId);
    });
  }, [uploads, handleQuotaError]);

  const value = useMemo<QuotaBlockContextValue>(
    () => ({ handleQuotaError, openExtractionAllowanceBlock }),
    [handleQuotaError, openExtractionAllowanceBlock],
  );

  return (
    <QuotaBlockCtx value={value}>
      {children}
      <QuotaBlockDialog
        allowance={refusal.allowance}
        onOpenChange={setOpen}
        open={open}
        resourceName={refusal.resource}
      />
    </QuotaBlockCtx>
  );
}
